// Import type augmentation for ApiErrorWithCode
import '@/types/query.types';

import type { Sort } from '@compass/client/types/common';
import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      // Disable React Query retries - axios-retry handles transient errors (429, 502, 503, 504)
      // Having both causes double retries (e.g., RQ retry 1 × axios 3 retries = 8 total requests)
      retry: false,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// Query key factory for consistent key generation
export const queryKeys = {
  all: ['compass'] as const,
  auth: {
    all: ['compass', 'auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
  },
  users: {
    all: ['compass', 'users'] as const,
    me: (organizationId?: string, programId?: string) =>
      [...queryKeys.users.all, 'me', organizationId, programId] as const,
  },
  programs: {
    all: ['compass', 'programs'] as const,
    byUser: () => [...queryKeys.programs.all, 'byUser'] as const,
  },
  workflows: {
    all: ['compass', 'workflows'] as const,
    byProgram: (programId: string) => [...queryKeys.workflows.all, 'byProgram', programId] as const,
  },
  cases: {
    all: ['compass', 'cases'] as const,
    byWorkflow: (
      workflowId: string,
      params?: {
        limit?: number;
        start_after_id?: string;
        states?: string[];
        referenceIds?: string[];
        caseIds?: string[];
        assigned?: string[];
        created_at_after?: string;
        created_at_before?: string;
        campaign_id?: string;
        payor_name?: string;
        sort_by_created_at?: Sort;
        sort_by_payor_name?: Sort;
      },
    ) => [...queryKeys.cases.all, 'byWorkflow', workflowId, params] as const,
    byId: (caseId: string) => [...queryKeys.cases.all, caseId] as const,
  },
  myCases: {
    all: ['compass', 'myCases'] as const,
    byOrg: (
      organizationId: string,
      params?: {
        program_ids?: string[];
        case_record_ids?: string[];
        created_after?: string;
        created_before?: string;
        limit?: number;
        start_after_id?: string;
      },
    ) => [...queryKeys.myCases.all, 'byOrg', organizationId, params] as const,
  },
  caseTasks: {
    byCase: (caseRecordId: string) => ['compass', 'caseTasks', caseRecordId] as const,
  },
  myTasks: {
    all: ['compass', 'myTasks'] as const,
    byOrg: (
      organizationId: string,
      params?: {
        created_after?: string;
        created_before?: string;
        pageSize?: number;
        maxPages?: number;
      },
    ) => [...queryKeys.myTasks.all, 'byOrg', organizationId, params] as const,
    openCount: (organizationId: string) =>
      [...queryKeys.myTasks.all, 'openCount', organizationId] as const,
  },
  orgTasks: {
    all: ['compass', 'orgTasks'] as const,
    byOrg: (
      organizationId: string,
      params?: {
        created_after?: string;
        created_before?: string;
        user_ids?: string[];
        task_id?: string;
        case_record_id?: string;
        pageSize?: number;
        maxPages?: number;
      },
    ) => [...queryKeys.orgTasks.all, 'byOrg', organizationId, params] as const,
  },
  campaigns: {
    all: ['compass', 'campaigns'] as const,
    byWorkflow: (
      workflowId: string,
      params?: {
        limit?: number;
        offset?: number;
      },
    ) => [...queryKeys.campaigns.all, 'byWorkflow', workflowId, params] as const,
    byId: (campaignId: string) => [...queryKeys.campaigns.all, campaignId] as const,
  },
  evaluations: {
    all: ['compass', 'evaluations'] as const,
    byOrganization: (
      organizationId: string,
      params?: {
        program_ids?: string[];
        call_ids?: string[];
        case_record_ids?: string[];
        agent_user_ids?: string[];
        evaluator_type?: number;
        scorecard_id?: string;
        limit?: number;
        start_after_id?: string;
        statuses?: number[];
        aggregate_score_gte?: number;
        aggregate_score_lte?: number;
        call_start_time_after?: string;
        call_start_time_before?: string;
        evaluated_at_after?: string;
        evaluated_at_before?: string;
        flag_escalation?: boolean;
        flag_compliance_failed?: boolean;
        needs_review?: boolean;
        only_ineligible?: boolean;
        only_reviewed?: boolean;
        call_directions?: number[];
        sort_by_call_start_time?: number;
        sort_by_aggregate_score?: number;
        sort_by_call_duration?: number;
        sort_by_section_score?: { section_id?: string; sort?: number };
        metadata?: { [key: string]: string };
        mode?: string;
      },
    ) => [...queryKeys.evaluations.all, 'byOrganization', organizationId, params] as const,
    countsByOrganization: (
      organizationId: string,
      params?: {
        program_ids?: string[];
        call_ids?: string[];
        agent_user_ids?: string[];
        call_directions?: number[];
        aggregate_score_gte?: number;
        aggregate_score_lte?: number;
        call_start_time_after?: string;
        call_start_time_before?: string;
        only_ineligible?: boolean;
        only_reviewed?: boolean;
        metadata?: { [key: string]: string };
        metadata_counts?: { [key: string]: string };
      },
    ) => [...queryKeys.evaluations.all, 'countsByOrganization', organizationId, params] as const,
    byId: (evaluationId: string) => [...queryKeys.evaluations.all, evaluationId] as const,
  },
  scorecards: {
    all: ['compass', 'scorecards'] as const,
    byProgram: (
      organizationId: string,
      programId: string,
      params?: {
        is_active?: boolean;
        limit?: number;
        start_after_id?: string;
      },
    ) => [...queryKeys.scorecards.all, 'byProgram', organizationId, programId, params] as const,
    byId: (scorecardId: string, programId?: string) =>
      [...queryKeys.scorecards.all, scorecardId, programId] as const,
  },
  schema: {
    all: ['compass', 'schema'] as const,
    // schemaVersion is appended only when given, so the workflow key stays a prefix of every version's key.
    byWorkflow: (orgId: string, progId: string, workflowId: string, schemaVersion?: string) =>
      schemaVersion
        ? ([...queryKeys.schema.all, orgId, progId, workflowId, schemaVersion] as const)
        : ([...queryKeys.schema.all, orgId, progId, workflowId] as const),
  },
  scripts: {
    all: ['compass', 'scripts'] as const,
    activeByWorkflow: (orgId: string, progId: string, workflowId: string) =>
      [...queryKeys.scripts.all, 'active', orgId, progId, workflowId] as const,
  },
  programUsers: {
    // One batch (≤100) of users' roles on a program (BV-2657 human-audit assignees). Kept
    // under the 'search' prefix so a role change (useUserRoles invalidates
    // `programUsers.searchAll()`) refetches it too.
    roles: (orgId: string, programId: string, userIds: string[]) =>
      ['compass', 'programUsers', 'search', 'roles', orgId, programId, userIds.join(',')] as const,
  },
  teams: {
    all: ['compass', 'teams'] as const,
    byOrganization: (orgId: string) => ['compass', 'teams', 'org', orgId] as const,
    byProgram: (orgId: string, programId: string) =>
      ['compass', 'teams', 'program', orgId, programId] as const,
    byId: (orgId: string, teamId: string) => ['compass', 'teams', orgId, teamId] as const,
    current: (userId: string) => ['compass', 'teams', 'current', userId] as const,
  },
  workforce: {
    all: ['compass', 'workforce'] as const,
    // Keyed by (org, program, assignee-CSV). The comma-joined assignee list
    // busts the cache when a team's membership changes; the prefix (org, program)
    // is used for reassign invalidation across all assignee variants of a program.
    casesByProgram: (orgId: string, programId: string, assigneeCsv: string) =>
      ['compass', 'workforce', 'cases', orgId, programId, assigneeCsv] as const,
    casesByProgramPrefix: (orgId: string, programId: string) =>
      ['compass', 'workforce', 'cases', orgId, programId] as const,
    // One payer's hours for a workflow type, fetched when a clock popover opens
    // and shared by every case with that payer and workflow type (BV-2525).
    payerHours: (orgId: string, programId: string, payerSlug: string, workflowType: string) =>
      ['compass', 'workforce', 'payer-hours', orgId, programId, payerSlug, workflowType] as const,
  },
  payors: {
    all: ['compass', 'payors', 'all'] as const,
  },
  config: {
    all: ['compass', 'config'] as const,
    featureFlags: () => [...queryKeys.config.all, 'feature_flags'] as const,
    settings: () => [...queryKeys.config.all, 'settings'] as const,
    featureFlag: (key: string) => [...queryKeys.config.all, 'feature_flag', key] as const,
  },
  mailbox: {
    all: ['compass', 'mailbox'] as const,
    inbox: (params?: { limit?: number; start_after_id?: string }) =>
      [...queryKeys.mailbox.all, 'inbox', params] as const,
    byId: (messageId: string) => [...queryKeys.mailbox.all, messageId] as const,
    unreadCount: () => [...queryKeys.mailbox.all, 'unreadCount'] as const,
  },
  feedback: {
    all: ['compass', 'feedback'] as const,
    byEvaluation: (evaluationId: string) =>
      [...queryKeys.feedback.all, 'byEvaluation', evaluationId] as const,
    byEvaluations: (organizationId: string, evaluationIds: string[]) =>
      [...queryKeys.feedback.all, 'byEvaluations', organizationId, evaluationIds] as const,
  },
  serviceAccounts: {
    all: ['compass', 'serviceAccounts'] as const,
    list: (includeDisabled?: boolean) =>
      [...queryKeys.serviceAccounts.all, 'list', includeDisabled ?? false] as const,
  },
  orgKeys: {
    all: ['compass', 'orgKeys'] as const,
    list: (organizationId: string) => [...queryKeys.orgKeys.all, 'list', organizationId] as const,
  },
  webhooks: {
    all: ['compass', 'webhooks'] as const,
    /** Everything for one org — invalidate after any endpoint or delivery mutation. */
    org: (organizationId: string) => [...queryKeys.webhooks.all, organizationId] as const,
    endpoints: (organizationId: string) =>
      [...queryKeys.webhooks.org(organizationId), 'endpoints'] as const,
    endpoint: (organizationId: string, endpointId: string) =>
      [...queryKeys.webhooks.org(organizationId), 'endpoint', endpointId] as const,
    health: (organizationId: string, endpointIds: string[]) =>
      [...queryKeys.webhooks.org(organizationId), 'health', endpointIds] as const,
    deliveries: (organizationId: string, filters: unknown, cursor: string | undefined) =>
      [...queryKeys.webhooks.org(organizationId), 'deliveries', filters, cursor ?? ''] as const,
    delivery: (organizationId: string, deliveryId: string) =>
      [...queryKeys.webhooks.org(organizationId), 'delivery', deliveryId] as const,
    operation: (organizationId: string, operationId: string) =>
      [...queryKeys.webhooks.org(organizationId), 'operation', operationId] as const,
  },
  langfuseBridge: {
    all: ['compass', 'langfuseBridge'] as const,
    tracesByCall: (callId: string) => [...queryKeys.langfuseBridge.all, 'traces', callId] as const,
    trace: (traceId: string) => [...queryKeys.langfuseBridge.all, 'trace', traceId] as const,
    traceMemberships: (traceId: string) =>
      [...queryKeys.langfuseBridge.all, 'traceMemberships', traceId] as const,
    datasetItem: (datasetId: string, itemId: string) =>
      [...queryKeys.langfuseBridge.all, 'datasetItem', datasetId, itemId] as const,
    datasets: () => [...queryKeys.langfuseBridge.all, 'datasets'] as const,
  },
};
